import { getClient, getLibraryFolders } from "@/api";
import { getLibraryDocument } from "@/api/library-documents/library-documents";
import {
    ILibraryListItem,
    LibraryTab,
} from "@/api/library-documents/library-documents.types";
import { Modal } from "@/components/shared/Modal";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { LIBRARY_SEARCH_DEBOUNCE_MS } from "@/pages/frontend/documents/constants";
import { DocumentList } from "@/pages/frontend/documents/components/DocumentList";
import { FilterBar } from "@/pages/frontend/documents/components/FilterBar";
import { LibraryTypeFilter } from "@/pages/frontend/documents/utils/libraryItemTypes";
import { Box } from "@mui/material";
import { useDebouncedEffect } from "@react-hookz/web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { buildClientRootedBreadcrumbs } from "./clientBreadcrumbs";
import {
    DocumentPickerValue,
    IBreadcrumbSegment,
    IDocumentPickerModalProps,
} from "./DocumentPicker.types";

const ACTIVE_TAB: LibraryTab = "documents";
const TYPE_FILTER: LibraryTypeFilter = "documents";
const noop = () => {};

export const DocumentPickerModal = ({
    open,
    onClose,
    onConfirm,
    clientId,
}: IDocumentPickerModalProps) => {
    const queryClient = useQueryClient();
    const { organization } = useOrganization();
    const orgId = organization.id;

    // Strict client scoping (insurescope-7149k): when opened from a client,
    // the picker is sealed to that client's folder subtree — it mirrors
    // ClientLibraryTab (browse sends folder_id rooted at the client root;
    // search sends flat client_id; folders shown only while browsing) with NO
    // escape to the wider library. The global flow (no clientId) is unchanged:
    // full-library browse, FilterBar's own path breadcrumbs, no scope strip.
    const clientScoped = clientId != null;

    const [folderId, setFolderId] = useState<number | null>(null);
    const [searchValue, setSearchValue] = useState("");
    const [selectedItem, setSelectedItem] = useState<ILibraryListItem | null>(
        null
    );
    const [isConfirming, setIsConfirming] = useState(false);
    const [confirmError, setConfirmError] = useState<string | null>(null);

    // The SINGLE search debounce (insurescope-2h145), mirroring the tab: the
    // mode flip and the requested result set derive from the same settled
    // value. Only consumed on the client-scoped path; the global path keeps
    // its original behavior (DocumentList debounces internally).
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useDebouncedEffect(
        () => setDebouncedSearch(searchValue),
        [searchValue],
        LIBRARY_SEARCH_DEBOUNCE_MS
    );
    const searchMode = debouncedSearch.trim().length > 0;

    const { data: foldersData } = useQuery({
        queryKey: ["frontend-library-folders"],
        queryFn: () => getLibraryFolders(),
        enabled: open,
        staleTime: 60_000,
    });
    // Memoized so the `?? []` fallback can't churn the breadcrumb memo every
    // render (mirrors ClientLibraryTab).
    const folders = useMemo(
        () => foldersData?.folders ?? [],
        [foldersData?.folders]
    );

    // Self-fetch the client for its root_folder_id + name (warm cache, shared
    // with ClientDetail/ClientLibraryTab) — avoids threading a new prop
    // through CreateScopeDialog -> DocumentSourceSection -> modal.
    const { data: client } = useQuery({
        queryKey: ["client", clientId],
        queryFn: () => getClient(clientId as number),
        enabled: open && clientScoped,
        staleTime: 60_000,
    });
    const rootFolderId = client?.root_folder_id ?? null;

    // Browse location: the client subtree defaults to the client root; the
    // global path keeps its raw folderId. `null` while browsing the client
    // means "at the root".
    const browseFolderId = clientScoped ? (folderId ?? rootFolderId) : folderId;
    // FilterBar's internal breadcrumb builder is the only consumer of
    // currentFolder; on the client path clientBreadcrumbs overrides it, so
    // pass null to skip FilterBar's discarded ancestor walk (mirrors the tab).
    const currentFolder = clientScoped
        ? null
        : (folders.find((f) => f.id === browseFolderId) ?? null);

    const handleItemSelectionChange = useCallback(
        (items: ILibraryListItem[]) => {
            setSelectedItem(items[0] ?? null);
        },
        []
    );

    const handleSearchClear = useCallback(() => {
        setSearchValue("");
        setDebouncedSearch("");
    }, []);

    // Folder navigation is a browse-intent declaration: it cancels any pending
    // search so the mode flip and the clicked folder's contents land together
    // (mirrors ClientLibraryTab). `null` returns to the client root.
    const handleClientFolderNavigate = useCallback((id: number | null) => {
        setSearchValue("");
        setDebouncedSearch("");
        setFolderId(id);
    }, []);

    // Client-rooted breadcrumbs through FilterBar's caller-supplied-segments
    // seam (shape/rules in buildClientRootedBreadcrumbs); undefined on the
    // global path so FilterBar keeps its own path breadcrumbs.
    const clientBreadcrumbs: IBreadcrumbSegment[] | undefined = useMemo(() => {
        if (!clientScoped) return undefined;
        return buildClientRootedBreadcrumbs({
            folders,
            rootFolderId,
            currentFolderId: browseFolderId,
            clientName: client?.name,
            searchMode,
            onNavigate: handleClientFolderNavigate,
        });
    }, [
        clientScoped,
        searchMode,
        client?.name,
        rootFolderId,
        browseFolderId,
        folders,
        handleClientFolderNavigate,
    ]);

    const handleClose = useCallback(() => {
        if (isConfirming) return;
        setFolderId(null);
        setSearchValue("");
        setDebouncedSearch("");
        setSelectedItem(null);
        setConfirmError(null);
        onClose();
    }, [isConfirming, onClose]);

    const handleConfirm = useCallback(async () => {
        if (!selectedItem) return;
        setIsConfirming(true);
        setConfirmError(null);
        try {
            const doc = await queryClient.ensureQueryData({
                queryKey: ["library-document", orgId, selectedItem.id],
                queryFn: () => getLibraryDocument(selectedItem.id),
                staleTime: 60_000,
            });
            if (!doc.object_name) {
                throw new Error("Document has no stored file");
            }
            const value: DocumentPickerValue = {
                source: "library",
                objectName: doc.object_name,
                libraryDocumentId: doc.id,
                title: doc.document_name,
                folderId: doc.library_folder_id,
                folderName: doc.folder?.name ?? null,
            };
            setIsConfirming(false);
            handleClose();
            onConfirm(value);
        } catch {
            setConfirmError(
                "Failed to load document details. Please try again."
            );
            setIsConfirming(false);
        }
    }, [selectedItem, queryClient, orgId, handleClose, onConfirm]);

    const actionLabel = isConfirming ? "Loading..." : "Select";

    // The list props split by path. The client path mirrors the tab's
    // mode flip; the global path is byte-for-byte the original behavior.
    const listFolderId = clientScoped
        ? searchMode
            ? null
            : browseFolderId
        : folderId;
    const listClientId = clientScoped && searchMode ? clientId : null;
    const listOnFolderNavigate = clientScoped
        ? handleClientFolderNavigate
        : setFolderId;

    // The client path gates the list until the client + folders resolve (no
    // fallback flash), and shows an empty state when the client has no library
    // root yet (nothing filed to browse; search still reaches tagged items).
    const clientFolderStateReady =
        !clientScoped || (client !== undefined && foldersData !== undefined);
    const clientNoRoot =
        clientScoped &&
        !searchMode &&
        client !== undefined &&
        rootFolderId === null;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Select a Document"
            maxWidth="lg"
            showCancel
            isLoading={isConfirming}
            error={confirmError}
            actions={[
                {
                    label: actionLabel,
                    onClick: handleConfirm,
                    disabled: !selectedItem || isConfirming,
                },
            ]}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "60vh",
                    minHeight: 400,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        flexShrink: 0,
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        bgcolor: "background.paper",
                    }}
                >
                    <FilterBar
                        activeTab={ACTIVE_TAB}
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        onSearchClear={handleSearchClear}
                        typeFilter={TYPE_FILTER}
                        onTypeFilterChange={noop}
                        currentFolder={currentFolder}
                        folders={folders}
                        onFolderNavigate={listOnFolderNavigate}
                        breadcrumbSegments={clientBreadcrumbs}
                        hideActions
                        hideFilters
                    />
                </Box>
                {clientScoped && (
                    <div className="text-muted-foreground bg-muted/40 mb-3 flex flex-shrink-0 items-center px-4 py-2 text-sm">
                        Showing {client?.name ?? "this client"}'s documents
                    </div>
                )}
                <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                    {!clientFolderStateReady ? null : clientNoRoot ? (
                        <div className="text-muted-foreground flex h-full items-center justify-center p-8 text-center text-sm">
                            No documents in {client?.name ?? "this client"}'s
                            library yet.
                        </div>
                    ) : (
                        <DocumentList
                            activeTab={ACTIVE_TAB}
                            folderId={listFolderId}
                            clientId={listClientId}
                            searchQuery={
                                clientScoped ? debouncedSearch : searchValue
                            }
                            searchQuerySettled={clientScoped}
                            typeFilter={TYPE_FILTER}
                            mode="select"
                            selectionMode="single"
                            tableDensity="compact"
                            onFolderNavigate={listOnFolderNavigate}
                            onItemSelectionChange={handleItemSelectionChange}
                            onSearchClear={handleSearchClear}
                            onDocumentAction={noop}
                            hideRowActions
                            hideClientColumn={clientScoped}
                            // Scope/comparison analysis can't consume
                            // spreadsheets (create_scope rejects them), so keep
                            // them out of this picker. The chat attachment
                            // picker deliberately shows them.
                            excludeSpreadsheets
                        />
                    )}
                </Box>
            </Box>
        </Modal>
    );
};
